/**
 * Admin User Management Module
 */

import { api } from '../api/client.js';
import { apiCall } from '../api/helpers.js';
import { escapeHtml, escapeAttr } from '../utils.js';
import { declareLoading, declareLoaded, confirmDialog, notificationDialog } from '../ui/dialogs.js';
import { generateToggleList, getToggleListSelected } from '../ui/forms.js';
import { setPassword } from '../user/settings.js';
import { getCurrentUser } from '../app/init.js';
import type { User, CreateUserRequest } from '../api/types.js';

// Module state
let usersById: Record<string, User> = {};
let usersTable: unknown = null;

/**
 * Generate access level select
 */
function generateAccessLevelSelect(selected: string = 'readonly', operatorMode: boolean = false): string {
  const levels: [string, string][] = operatorMode
    ? [['readonly', 'User']]
    : [['readonly', 'User'], ['operator', 'Operator'], ['admin', 'Admin']];
  const options = levels.map(([value, label]) =>
    `<option value="${escapeAttr(value)}" ${value === selected ? 'selected' : ''}>${escapeHtml(label)}</option>`
  ).join('');
  return `<select id="access_level" name="access_level">${options}</select>`;
}


/**
 * Delete a user
 */
export async function deleteUser(username: string): Promise<void> {
  confirmDialog(`Are you sure you want to delete user '${escapeHtml(username)}'?`, async () => {
    declareLoading();
    await apiCall(api.deleteUser(username), 'Failed to Delete User');
    await fillUsersTable();
    declareLoaded();
  });
}

/**
 * Modify an existing user
 */
export async function modifyUser(username: string): Promise<void> {
  const user = usersById[username];
  if (!user) {
    notificationDialog('User not found');
    return;
  }

  const isOperator = getCurrentUser()?.role === 'operator';

  // Fetch VM list and (for admins) organizations for toggle selectors
  const fetches: [Promise<unknown>, Promise<unknown>] = [
    apiCall(api.listVms(), 'Failed to Load VMs').then(r => r ?? []),
    isOperator ? Promise.resolve(null) : apiCall(api.listOrganizations(), 'Failed to Load Organizations'),
  ];
  const [vmsList, orgsResponse] = await Promise.all(fetches) as [typeof undefined extends never ? never : Awaited<ReturnType<typeof api.listVms>>, Awaited<ReturnType<typeof api.listOrganizations>> | null];
  const vms = (vmsList ?? []) as { id: number; name: string }[];
  vms.sort((a, b) => a.id - b.id);

  const vmNames = vms.map(vm => vm.name);
  const nameToId: Record<string, number> = {};
  for (const vm of vms) nameToId[vm.name] = vm.id;
  const selectedIds = new Set(user.vm_access || []);
  const selectedNames = vms.filter(vm => selectedIds.has(vm.id)).map(vm => vm.name);

  // Organization toggle data (admin only)
  let orgToggleHtml = '';
  if (!isOperator && orgsResponse && orgsResponse.organizations.length > 0) {
    const orgNames = orgsResponse.organizations.map(o => o.name);
    const userOrgNames = user.organizations || [];
    const orgLabels: Record<string, string> = {};
    for (const o of orgsResponse.organizations) {
      orgLabels[o.name] = o.description || o.name;
    }
    orgToggleHtml = `<h5 style="grid-column: 1 / -1; margin: 8px 0 4px;">Organizations</h5>`
      + generateToggleList('user_organizations', orgNames, userOrgNames, orgLabels);
  }

  // API key buttons (admin only)
  let apiKeyHtml = '';
  if (!isOperator) {
    apiKeyHtml = user.has_api_key
      ? `<button class="ui-button" id="btn-regen-api-key">Regenerate API Key</button>
         <button class="ui-button" id="btn-revoke-api-key">Revoke API Key</button>`
      : `<button class="ui-button" id="btn-gen-api-key">Generate API Key</button>`;
  }

  // Role selector (admin only)
  const roleSelectorHtml = isOperator ? '' :
    `<label>Access Level</label><div class="form-value">${generateAccessLevelSelect((user.role as string) || 'readonly')}</div>`;

  const form = `<div class="vm-form">
    ${roleSelectorHtml}
    <h5 style="grid-column: 1 / -1; margin: 8px 0 4px;">VM Access</h5>
    ${generateToggleList('vm_access', vmNames, selectedNames)}
    <p style="font-size: 0.9em; color: #666; grid-column: 1 / -1;">Select which VMs this user can access. Admins always have access to all VMs.</p>
    ${orgToggleHtml}
    <div class="form-buttons">
      <button class="ui-button" id="btn-change-email">Change E-Mail</button>
      <button class="ui-button" id="btn-reset-password">Reset Password</button>
      ${apiKeyHtml}
    </div>
  </div>`;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Modify User</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Modify User',
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        Save: async function () {
          const params: Record<string, unknown> = {
            vm_access: getToggleListSelected('vm_access').map(name => nameToId[name]),
          };
          // Only admins send role and organizations
          if (!isOperator) {
            params.username = username;
            params.role = $('#access_level').val() as 'admin' | 'operator' | 'readonly';
            params.organizations = getToggleListSelected('user_organizations');
          }
          const parent = $(this);
          declareLoading();
          const result = await apiCall(api.updateUser(username, params), 'Failed to Update User');
          declareLoaded();
          if (result) {
            parent.dialog('close');
            fillUsersTable();
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
      open: function () {
        $('#btn-change-email').on('click', () => setEmail(username));
        $('#btn-reset-password').on('click', () => setPassword(username));
        if (!isOperator) {
          const modifyDlg = $(this) as JQuery;
          $('#btn-gen-api-key, #btn-regen-api-key').on('click', () => generateApiKey(username, modifyDlg));
          $('#btn-revoke-api-key').on('click', () => revokeApiKey(username, modifyDlg));
        }
      },
    });
}

/**
 * Create a new user
 */
export async function createUser(): Promise<void> {
  const isOperator = getCurrentUser()?.role === 'operator';

  // Fetch organizations for toggle selector (admin only — operators auto-inherit orgs)
  let orgToggleHtml = '';
  if (!isOperator) {
    const orgsResponse = await apiCall(api.listOrganizations(), 'Failed to Load Organizations');
    if (orgsResponse && orgsResponse.organizations.length > 0) {
      const orgNames = orgsResponse.organizations.map(o => o.name);
      const orgLabels: Record<string, string> = {};
      for (const o of orgsResponse.organizations) {
        orgLabels[o.name] = o.description || o.name;
      }
      orgToggleHtml = `<h5 style="grid-column: 1 / -1; margin: 8px 0 4px;">Organizations</h5>`
        + generateToggleList('user_organizations', orgNames, [], orgLabels);
    }
  }

  // Role selector (admin only — operators always create readonly users)
  const roleSelectorHtml = isOperator ? '' :
    `<label>Access Level</label><div class="form-value">${generateAccessLevelSelect()}</div>`;

  const form = `<div class="vm-form">
    <label>Username</label><div class="form-value"><input autocomplete='username' id='username' name='username'></div>
    <label>E-Mail</label><div class="form-value"><input autocomplete='email' id='email' name='email'></div>
    ${roleSelectorHtml}
    ${orgToggleHtml}
    <label>Setup Method</label>
    <div class="form-value">
      <label><input type="radio" name="setup_method" value="invite" checked> Send invitation email</label><br>
      <label><input type="radio" name="setup_method" value="password"> Set password now</label>
    </div>
    <div id="password-fields" style="display: none; grid-column: 1 / -1;">
      <label>Password</label>
      <div class="form-value"><input autocomplete='new-password' type='password' id='password' name='password'></div>
    </div>
    <p id="invite-note" style="font-size: 0.9em; color: #666; grid-column: 1 / -1;">An invitation email will be sent to the user to set their password.</p>
  </div>`;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Create User</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Create User',
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        Create: async function () {
          const user = $('#username').val() as string;
          const email = $('#email').val() as string;
          const method = $('input[name="setup_method"]:checked').val() as string;

          if (!user) {
            alert('Please enter a username');
            return;
          }
          if (method === 'invite' && !email) {
            alert('Email is required to send an invitation');
            return;
          }
          if (method === 'password') {
            const password = $('#password').val() as string;
            if (!password) {
              alert('Please enter a password');
              return;
            }
          }

          const params: CreateUserRequest = {
            username: user,
          };
          // Only admins set role and organizations
          if (!isOperator) {
            params.role = $('#access_level').val() as 'admin' | 'operator' | 'readonly';
            params.organizations = getToggleListSelected('user_organizations');
          }
          if (email) params.email = email;
          if (method === 'password') {
            params.password = $('#password').val() as string;
          }

          const parent = $(this);
          declareLoading();
          const result = await apiCall(api.createUser(params), 'Failed to Create User');
          if (result) {
            if (method === 'invite' && email) {
              await apiCall(api.sendUserInvite(user), 'Failed to Send Invite');
            }
            declareLoaded();
            parent.dialog('close');
            fillUsersTable();
          } else {
            declareLoaded();
          }
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
      open: function () {
        $('input[name="setup_method"]').on('change', function () {
          const isPassword = $(this).val() === 'password';
          $('#password-fields').toggle(isPassword);
          $('#invite-note').toggle(!isPassword);
        });
      },
    });
}

/**
 * Set email for a user
 */
export async function setEmail(username: string): Promise<void> {
  const form = `
    <label>E-Mail</label><input id='email' name='email' type='email'><br>
  `;

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Change E-Mail</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Change E-Mail',
      zIndex: 10000,
      autoOpen: true,
      width: 'auto',
      resizable: false,
      buttons: {
        'Set E-Mail': async function () {
          const email = $('#email').val() as string;
          const parent = $(this);
          declareLoading();
          const result = await apiCall(api.updateUser(username, { email }), 'Failed to Set Email');
          declareLoaded();
          if (result) parent.dialog('close');
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Reset password for a user (admin action)
 */
export async function resetUserPassword(username: string): Promise<void> {
  if (!confirm(`Send password reset email to ${username}?`)) {
    return;
  }
  declareLoading();
  const result = await apiCall(api.adminResetPassword(username), 'Failed to Reset Password');
  declareLoaded();
  if (result !== null) alert('Password reset email sent.');
}

/**
 * Resend invitation email
 */
export async function resendUserInvite(username: string): Promise<void> {
  if (!confirm(`Resend invitation email to ${username}?`)) {
    return;
  }
  declareLoading();
  const result = await apiCall(api.sendUserInvite(username), 'Failed to Send Invite');
  declareLoaded();
  if (result !== null) alert('Invitation email sent.');
}

/**
 * Generate an API key for a user and display it for copying.
 * The key is only shown once — the server stores only the hash.
 */
async function generateApiKey(username: string, parentDialog: JQuery): Promise<void> {
  const isRegen = usersById[username]?.has_api_key;
  if (isRegen && !confirm(`This will replace the existing API key for ${username}. The old key will stop working immediately. Continue?`)) {
    return;
  }

  declareLoading();
  const result = await apiCall(api.generateApiKey(username), 'Failed to Generate API Key');
  declareLoaded();
  if (!result) return;

  const key = result.api_key;

  $('<div></div>')
    .appendTo('body')
    .html(`<div>
      <p>API key generated for <strong>${escapeHtml(username)}</strong>. Copy it now — it will not be shown again.</p>
      <div style="display: flex; gap: 6px; align-items: center; margin-top: 8px;">
        <input id="api-key-value" type="text" readonly value="${escapeAttr(key)}"
               style="font-family: monospace; flex: 1; padding: 6px; font-size: 0.9em;">
        <button class="ui-button" id="btn-copy-api-key">Copy</button>
      </div>
    </div>`)
    .dialog({
      modal: true,
      title: 'API Key Generated',
      zIndex: 10001,
      autoOpen: true,
      width: 520,
      resizable: false,
      buttons: {
        Close: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
        parentDialog.dialog('close');
        fillUsersTable();
      },
      open: function () {
        $('#btn-copy-api-key').on('click', () => {
          const input = document.getElementById('api-key-value') as HTMLInputElement;
          navigator.clipboard.writeText(input.value).then(() => {
            $('#btn-copy-api-key').text('Copied!');
          }).catch(() => {
            input.select();
            document.execCommand('copy');
            $('#btn-copy-api-key').text('Copied!');
          });
        });
        // Auto-select the key text for easy copying
        ($('#api-key-value')[0] as HTMLInputElement).select();
      },
    });
}

/**
 * Revoke a user's API key
 */
async function revokeApiKey(username: string, parentDialog: JQuery): Promise<void> {
  confirmDialog(`Revoke the API key for '${escapeHtml(username)}'? Any tools using this key will lose access.`, async () => {
    declareLoading();
    const result = await apiCall(api.revokeApiKey(username), 'Failed to Revoke API Key');
    declareLoaded();
    if (result !== null) {
      parentDialog.dialog('close');
      fillUsersTable();
    }
  });
}

/**
 * Fill the users table
 */
export async function fillUsersTable(): Promise<void> {
  const [response, vmsList] = await Promise.all([
    apiCall(api.listUsersWithMeta(), 'Failed to Load Users'),
    apiCall(api.listVms(), 'Failed to Load VMs'),
  ]);
  if (!response) return;

  // Build VM ID → name lookup
  const vmNameById: Record<number, string> = {};
  for (const vm of (vmsList ?? [])) {
    vmNameById[vm.id] = vm.name;
  }

  const users = response.users;

  // Build the new lookup map before clearing the old one, so that
  // modifyUser() never sees an empty map mid-refresh (race with the
  // un-awaited fillUsersTable() call in createUser).
  const newUsersById: Record<string, User> = {};

  const tableData = users.map((user) => {
    const safeUsername = escapeAttr(user.username);
    const isActivated = user.activated !== false;
    const resetBtn = isActivated
      ? `<button class="ui-button" onclick='resetUserPassword("${safeUsername}")'>Reset Password</button>`
      : `<button class="ui-button" onclick='resendUserInvite("${safeUsername}")'>Resend Invite</button>`;

    const actions = `
      <button class="ui-button" onclick='modifyUser("${safeUsername}")'>Modify</button>
      ${resetBtn}
      <button class="ui-button" onclick='deleteUser("${safeUsername}")'>Delete</button>
    `;

    newUsersById[user.username] = user;

    // Resolve VM access to display string
    let vmAccess: string;
    if (user.role === 'admin') {
      vmAccess = 'All';
    } else if (user.vm_access && user.vm_access.length > 0) {
      vmAccess = user.vm_access
        .map(id => escapeHtml(vmNameById[id] || `#${id}`))
        .join(', ');
    } else {
      vmAccess = 'None';
    }

    const roleLabels: Record<string, string> = { admin: 'Admin', operator: 'Operator', readonly: 'User' };
    return {
      username: user.username,
      email: user.email || '',
      access_level: roleLabels[user.role] || user.role,
      vm_access: vmAccess,
      actions: actions,
    };
  });

  // Swap atomically so lookups never see an empty map
  usersById = newUsersById;

  if (!usersTable) {
    usersTable = $('#users').DataTable({
      data: tableData,
      columns: [
        { data: 'username', title: 'User Name' },
        { data: 'email', title: 'E-Mail' },
        { data: 'access_level', title: 'Access Level' },
        { data: 'vm_access', title: 'VM Access' },
        { data: 'actions', title: 'Actions' },
      ],
      paging: false,
      info: false,
      searching: false,
    });
  } else {
    const dt = usersTable as { clear: () => unknown; rows: { add: (data: unknown) => unknown }; draw: () => void };
    dt.clear();
    dt.rows.add(tableData);
    dt.draw();
  }

  declareLoaded();
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).fillUsersTable = fillUsersTable;
  (window as unknown as Record<string, unknown>).createUser = createUser;
  (window as unknown as Record<string, unknown>).modifyUser = modifyUser;
  (window as unknown as Record<string, unknown>).deleteUser = deleteUser;
  (window as unknown as Record<string, unknown>).setEmail = setEmail;
  (window as unknown as Record<string, unknown>).resetUserPassword = resetUserPassword;
  (window as unknown as Record<string, unknown>).resendUserInvite = resendUserInvite;
}
